import React, { useCallback, useEffect, useState } from 'react';
import { FaEdit, FaGasPump, FaIdCard, FaPlus, FaSave, FaSpinner, FaStore, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { fuelService, stationService } from '../services/refuelService';
import { stationClaimService } from '../services/stationClaimService';
import { ClaimStatus, FuelStation, STATION_FUEL_TYPES, StationClaim, StationFuelType } from '../types';
import { formatCNPJ, formatDate, formatDateTime, formatFuelType } from '../utils/formatters';

const CLAIM_LABELS: Record<ClaimStatus, { label: string; className: string }> = {
  PENDENTE: { label: 'Aguardando aprovação', className: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' },
  APROVADO: { label: 'Aprovado', className: 'bg-[#089453]/15 text-[#84cc34] border-[#089453]/30' },
  REJEITADO: { label: 'Recusado', className: 'bg-red-500/15 text-red-300 border-red-500/30' },
};

const MIN_PRICE = 0.5;
const MAX_PRICE = 30;

type PriceInputs = Partial<Record<StationFuelType, string>>;

// Preços da pesquisa ANP não são do posto: ficam fora do formulário
const ownFuels = (station: FuelStation) => (station.combustiveis || []).filter((fuel) => fuel.precoOrigem !== 'ANP');

const toInputs = (station: FuelStation): PriceInputs => {
  const inputs: PriceInputs = {};
  ownFuels(station).forEach((fuel) => {
    inputs[fuel.tipoCombustivel as StationFuelType] = fuel.precoAtual.toFixed(2).replace('.', ',');
  });
  return inputs;
};

const parsePrice = (value: string) => Number(value.replace(',', '.'));

const StationPrices: React.FC<{ station: FuelStation; onSaved: () => void }> = ({ station, onSaved }) => {
  const [inputs, setInputs] = useState<PriceInputs>(() => toInputs(station));
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => setInputs(toInputs(station)), [station]);

  const lastUpdate = ownFuels(station)
    .map((fuel) => fuel.precoAtualizadoEm || fuel.updatedAt)
    .filter(Boolean)
    .sort()
    .pop();

  const handleSave = async () => {
    const precos: { tipoCombustivel: StationFuelType; preco: number }[] = [];
    for (const tipo of STATION_FUEL_TYPES) {
      const raw = (inputs[tipo] || '').trim();
      if (!raw) continue;
      const preco = parsePrice(raw);
      if (Number.isNaN(preco) || preco < MIN_PRICE || preco > MAX_PRICE) {
        toast.error(`Preço inválido para ${formatFuelType(tipo)}`);
        return;
      }
      precos.push({ tipoCombustivel: tipo, preco });
    }

    // Combustível que tinha preço e foi apagado: o posto deixou de vender
    const removed = ownFuels(station).filter(
      (fuel) => !(inputs[fuel.tipoCombustivel as StationFuelType] || '').trim()
    );

    if (precos.length === 0 && removed.length === 0) {
      toast.error('Informe ao menos um preço');
      return;
    }

    setIsSaving(true);
    try {
      if (precos.length > 0) {
        await stationService.updatePrices(station.id, { precos });
      }
      await Promise.all(removed.map((fuel) => fuelService.deleteFuel(fuel.id)));
      toast.success('Preços atualizados');
      onSaved();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao salvar preços');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="mt-5 pt-5 border-t border-white/10">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
        <h4 className="text-white font-semibold flex items-center gap-2">
          <FaGasPump className="text-[#089453]" /> Preços (R$/litro)
        </h4>
        <span className="text-xs text-white/50">
          {lastUpdate ? `Última atualização: ${formatDateTime(lastUpdate)}` : 'Nenhum preço publicado ainda'}
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {STATION_FUEL_TYPES.map((tipo) => (
          <label key={tipo} className="block">
            <span className="block text-[#888] text-xs font-medium mb-1">{formatFuelType(tipo)}</span>
            <input
              inputMode="decimal"
              value={inputs[tipo] || ''}
              onChange={(e) => setInputs({ ...inputs, [tipo]: e.target.value.replace(/[^\d,.]/g, '') })}
              placeholder="Não vende"
              className="w-full px-3 py-2 bg-[#0f1419] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#089453]"
            />
          </label>
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
        <p className="text-xs text-white/50">
          Deixe em branco os combustíveis que o posto não vende.
        </p>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-br from-[#089453] to-[#84cc34] text-[#0f1419] text-sm font-semibold disabled:opacity-60"
        >
          {isSaving ? <FaSpinner className="animate-spin" /> : <FaSave />}
          Salvar preços
        </button>
      </div>
    </div>
  );
};

const ClaimModal: React.FC<{ isOpen: boolean; onClose: () => void; onSent: () => void }> = ({
  isOpen,
  onClose,
  onSent,
}) => {
  const [cnpj, setCnpj] = useState('');
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (isOpen) setCnpj('');
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (cnpj.replace(/\D/g, '').length !== 14) {
      toast.error('Informe um CNPJ válido');
      return;
    }
    setIsSending(true);
    try {
      await stationClaimService.requestByCnpj(cnpj);
      toast.success('Solicitação enviada. Avisaremos quando for aprovada.');
      onSent();
      onClose();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao enviar solicitação');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <form
        onSubmit={handleSubmit}
        className="bg-gradient-to-br from-[#1a2332] to-[#253547] border border-white/10 rounded-2xl p-6 md:p-8 max-w-lg w-full"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white flex items-center gap-3">
            <FaIdCard className="text-[#089453]" /> Solicitar meu posto
          </h2>
          <button type="button" onClick={onClose} className="text-[#888] hover:text-white">
            <FaTimes size={20} />
          </button>
        </div>
        <p className="text-sm text-white/60 mb-5">
          Seu posto pode já estar na nossa base, com dados públicos da ANP. Informe o CNPJ do seu posto para
          assumi-lo. Nossa equipe confere e aprova o pedido; depois disso o posto aparece no app e você publica
          os preços.
        </p>
        <label className="block text-[#888] text-sm font-medium mb-2">CNPJ do posto</label>
        <input
          value={cnpj}
          onChange={(e) => setCnpj(e.target.value)}
          placeholder="00.000.000/0000-00"
          className="w-full px-4 py-3 bg-[#0f1419] border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#089453]"
          autoFocus
        />
        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-3 rounded-xl border border-white/10 text-white/80 hover:border-white/30"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSending}
            className="flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-br from-[#089453] to-[#84cc34] text-[#0f1419] font-semibold disabled:opacity-60"
          >
            {isSending && <FaSpinner className="animate-spin" />}
            Enviar solicitação
          </button>
        </div>
      </form>
    </div>
  );
};

const Stations: React.FC = () => {
  const [stations, setStations] = useState<FuelStation[]>([]);
  const [claims, setClaims] = useState<StationClaim[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const [showClaim, setShowClaim] = useState(false);

  const load = useCallback(async () => {
    try {
      const [stationsResponse, claimsResponse] = await Promise.all([
        stationService.getMyStations(),
        stationClaimService.getMyClaims(),
      ]);
      setStations(stationsResponse.data || []);
      setClaims(claimsResponse.data || []);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao carregar seus postos');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNewStation = () => navigate('/postos/novo');

  const openEditStation = (station: FuelStation) => navigate(`/postos/${station.id}/editar`);

  // Pedidos aprovados já aparecem como posto; mostra só os em análise e recusados
  const openClaims = claims.filter((claim) => claim.status !== 'APROVADO');

  return (
    <div className="min-h-screen bg-[#0f1419] pb-24 lg:pb-0">
      <Header />

      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">Meus postos</h1>
            <p className="text-white/60 text-sm mt-1">Mantenha os preços atualizados para aparecer melhor no app.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowClaim(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-white/15 text-white hover:border-[#089453]"
            >
              <FaIdCard /> Já tenho posto no app
            </button>
            <button
              onClick={openNewStation}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-br from-[#089453] to-[#84cc34] text-[#0f1419] font-semibold"
            >
              <FaPlus /> Cadastrar novo posto
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20 text-[#089453]">
            <FaSpinner className="animate-spin" size={32} />
          </div>
        ) : (
          <>
            {openClaims.length > 0 && (
              <section className="mb-8">
                <h2 className="text-lg font-semibold text-white mb-3">Solicitações</h2>
                <div className="grid gap-3">
                  {openClaims.map((claim) => (
                    <div key={claim.id} className="p-4 rounded-xl bg-[#1a2332] border border-white/10">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <p className="text-white font-medium">{claim.postoNome}</p>
                          <p className="text-xs text-white/50">
                            {[claim.postoCnpj && formatCNPJ(claim.postoCnpj), claim.postoCidade && `${claim.postoCidade}/${claim.postoEstado}`]
                              .filter(Boolean)
                              .join(' · ')}
                            {' · '}
                            {claim.novoPosto ? 'Novo cadastro' : 'Posto existente'} · enviado em {formatDate(claim.createdAt)}
                          </p>
                        </div>
                        <span className={`text-xs px-2.5 py-1 rounded-full border ${CLAIM_LABELS[claim.status].className}`}>
                          {CLAIM_LABELS[claim.status].label}
                        </span>
                      </div>
                      {claim.status === 'REJEITADO' && claim.motivoRejeicao && (
                        <p className="text-sm text-red-300/90 mt-2">Motivo: {claim.motivoRejeicao}</p>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {stations.length === 0 ? (
              <div className="text-center py-16 px-6 rounded-2xl bg-[#1a2332] border border-white/10">
                <FaStore className="mx-auto text-[#089453] mb-4" size={40} />
                <h2 className="text-white text-lg font-semibold mb-2">Você ainda não tem postos aprovados</h2>
                <p className="text-white/60 text-sm max-w-md mx-auto">
                  Se o seu posto já aparece no app, solicite-o pelo CNPJ. Se não, cadastre um novo posto. Depois da
                  aprovação você pode publicar os preços aqui.
                </p>
              </div>
            ) : (
              <section className="grid gap-5">
                {stations.map((station) => (
                  <article key={station.id} className="p-5 md:p-6 rounded-2xl bg-[#1a2332] border border-white/10">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex items-start gap-4">
                        {station.fotoUrl && (
                          <img
                            src={station.fotoUrl}
                            alt={`Foto do ${station.nomePosto}`}
                            className="w-20 h-16 rounded-lg object-cover border border-white/10 shrink-0"
                          />
                        )}
                        <div>
                          <h3 className="text-white text-lg font-semibold flex items-center gap-2">
                            {station.nomePosto}
                            {station.ativo === false && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/60">Inativo</span>
                            )}
                          </h3>
                          <p className="text-sm text-white/60">{station.enderecoCompleto || station.endereco}</p>
                          {station.cnpj && <p className="text-xs text-white/40 mt-1">CNPJ {formatCNPJ(station.cnpj)}</p>}
                        </div>
                      </div>
                      <button
                        onClick={() => openEditStation(station)}
                        className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg border border-white/10 text-white/80 hover:border-white/30"
                      >
                        <FaEdit /> Editar dados
                      </button>
                    </div>
                    <StationPrices station={station} onSaved={load} />
                  </article>
                ))}
              </section>
            )}
          </>
        )}
      </main>

      <ClaimModal isOpen={showClaim} onClose={() => setShowClaim(false)} onSent={load} />
    </div>
  );
};

export default Stations;
